import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { CheckCircle, Crosshair, MapPin, Receipt, ThumbsUp, ThumbsDown, Camera, ArrowRight, Path } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api/public`;

// Custom Safety-Orange pin icon (Leaflet default icons 404 in CRA builds)
const pinIcon = new L.DivIcon({
  className: "",
  html: '<div style="width:22px;height:22px;background:#22c55e;border:3px solid #000;border-radius:50%;box-shadow:0 0 0 3px rgba(34,197,94,0.3);"></div>',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

export default function PublicReceiptPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submittingFb, setSubmittingFb] = useState(false);
  const [feedback, setFeedback] = useState(null); // local state after submit
  const [comment, setComment] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/receipt/${token}`);
        const j = await r.json();
        if (cancelled) return;
        if (!r.ok) {
          setError(j.detail || "Receipt unavailable");
        } else {
          setData(j);
          if (j.existing_feedback) setFeedback(j.existing_feedback);
        }
      } catch (e) {
        if (!cancelled) setError("Network error — please try again");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submitFeedback = async (rating) => {
    setSubmittingFb(true);
    try {
      const r = await fetch(`${API}/receipt/${token}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rating, comment: comment.trim() || null }),
      });
      const j = await r.json();
      if (r.ok) setFeedback({ rating, comment: comment.trim() || null });
      else setError(j.detail || "Couldn't save feedback");
    } catch (_e) {
      setError("Network error");
    } finally {
      setSubmittingFb(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-[#71717a] font-mono text-sm" data-testid="receipt-loading">
        Loading receipt…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center" data-testid="receipt-error">
        <div className="max-w-md text-center px-6">
          <Receipt size={42} className="text-[#ef4444] mx-auto mb-3" weight="bold" />
          <h1 className="font-heading font-black text-2xl mb-2 text-white">Receipt unavailable</h1>
          <p className="text-[#a1a1aa] text-sm">{error || "This receipt link is no longer valid."}</p>
        </div>
      </div>
    );
  }

  const { company_name, job_number, customer, dropoff_address, completion, photos, more_deliveries } = data;
  const completedDate = completion?.completed_at ? new Date(completion.completed_at) : null;
  const hasGps = completion && completion.lat != null && completion.lng != null;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#e4e4e7]" data-testid="public-receipt-page">
      <div className="max-w-2xl mx-auto px-4 py-8 sm:py-12">
        {/* Header */}
        <div className="mb-6 border-l-4 border-[#ff8c00] pl-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#a1a1aa]">{company_name} · Delivery Receipt</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mt-1 text-white">
            <CheckCircle size={28} weight="fill" className="text-[#22c55e] inline mr-2 -mt-1" />
            Delivery Complete
          </h1>
          <div className="text-[12px] font-mono text-[#a1a1aa] mt-1">{job_number} &middot; {customer}</div>
        </div>

        {/* Dropoff card */}
        <Card label="Dropoff Address" testid="receipt-dropoff">
          <div className="flex items-start gap-2 text-sm text-white">
            <MapPin size={16} className="text-[#ff8c00] mt-0.5 shrink-0" weight="bold" />
            <span>{dropoff_address}</span>
          </div>
          {completedDate && (
            <div className="text-[11px] text-[#71717a] font-mono mt-2 uppercase tracking-[0.18em]">
              Completed {completedDate.toLocaleString()}
            </div>
          )}
        </Card>

        {/* GPS map */}
        {hasGps && (
          <Card label="Verified GPS Pin" accent="#22c55e" testid="receipt-gps-map">
            <div className="h-56 sm:h-64 -mx-4 sm:mx-0 mt-1 mb-2 border border-[#22c55e]/30 overflow-hidden">
              <MapContainer center={[completion.lat, completion.lng]} zoom={16} className="h-full w-full" zoomControl={false} scrollWheelZoom={false}>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={[completion.lat, completion.lng]} icon={pinIcon}>
                  <Popup>Driver was here<br />{completion.lat.toFixed(5)}, {completion.lng.toFixed(5)}</Popup>
                </Marker>
              </MapContainer>
            </div>
            <a
              href={`https://maps.google.com/?q=${completion.lat},${completion.lng}`}
              target="_blank"
              rel="noreferrer"
              className="text-[11px] text-[#22c55e] hover:underline font-mono flex items-center gap-1"
              data-testid="receipt-gps-link"
            >
              <Crosshair size={11} weight="bold" />
              {completion.lat.toFixed(5)}, {completion.lng.toFixed(5)}
              {completion.accuracy_m != null ? ` · ±${Math.round(completion.accuracy_m)} m` : ""}
              <span className="text-[#71717a] ml-1">· View on Google Maps →</span>
            </a>
          </Card>
        )}

        {/* POD photos (watermarked) */}
        {photos && photos.length > 0 && (
          <Card label={`Proof of Delivery · ${photos.length} photo${photos.length === 1 ? "" : "s"}`} accent="#22c55e" testid="receipt-photos">
            <p className="text-[11px] text-[#71717a] mb-3 leading-relaxed">
              Each image is GPS-watermarked at the moment of capture for tamper-evident audit.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {photos.map((p) => (
                <a
                  key={p.id}
                  href={`${API}/receipt/${token}/photo/${p.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="block border border-[#22c55e]/40 hover:border-[#22c55e] transition-colors aspect-square"
                  data-testid={`receipt-photo-${p.id}`}
                >
                  <img
                    src={`${API}/receipt/${token}/photo/${p.id}`}
                    alt={`POD ${p.kind}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </a>
              ))}
            </div>
          </Card>
        )}

        {/* Feedback (NPS) */}
        <Card label="How was this delivery?" accent="#ff8c00" testid="receipt-feedback">
          {feedback ? (
            <div className="flex items-center gap-3 py-2" data-testid="receipt-feedback-thanks">
              <CheckCircle size={20} weight="fill" className="text-[#22c55e]" />
              <div>
                <div className="font-bold text-sm text-white">Thanks for your feedback!</div>
                <div className="text-[11px] text-[#a1a1aa] flex items-center gap-1 mt-0.5">
                  You rated this delivery {feedback.rating === "up" ? "👍 positive" : "👎 needs attention"}.
                  {feedback.rating === "down" && " The dispatch team has been notified."}
                </div>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-[11px] text-[#a1a1aa] mb-3 leading-relaxed">
                One tap helps the team improve. Add a comment if anything stood out.
              </p>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="Optional comment…"
                className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm mb-3 font-mono"
                data-testid="receipt-feedback-comment"
              />
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => submitFeedback("up")}
                  disabled={submittingFb}
                  className="bg-[#22c55e] hover:bg-[#22c55e]/90 disabled:opacity-40 text-black font-bold py-3 text-[11px] uppercase tracking-[0.2em] flex items-center justify-center gap-2"
                  data-testid="receipt-feedback-up"
                >
                  <ThumbsUp size={14} weight="fill" /> Great
                </button>
                <button
                  onClick={() => submitFeedback("down")}
                  disabled={submittingFb}
                  className="bg-[#1a1a1a] border border-[#ef4444] hover:bg-[#ef4444] hover:text-black text-[#ef4444] font-bold py-3 text-[11px] uppercase tracking-[0.2em] flex items-center justify-center gap-2"
                  data-testid="receipt-feedback-down"
                >
                  <ThumbsDown size={14} weight="fill" /> Issue
                </button>
              </div>
            </div>
          )}
        </Card>

        {/* More deliveries */}
        {more_deliveries && more_deliveries.length > 0 && (
          <Card label={`Your other deliveries from ${company_name}`} testid="receipt-more">
            <div className="divide-y divide-[#2a2a2a]">
              {more_deliveries.map((d) => (
                <Link
                  key={d.id}
                  to={`/receipt/${d.receipt_token}`}
                  className="flex items-center justify-between gap-3 py-2 hover:bg-[#1a1a1a] px-2 -mx-2 transition-colors"
                  data-testid={`receipt-more-${d.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-mono text-[11px] text-[#ff8c00]">{d.job_number}</div>
                    <div className="text-[12px] text-white truncate">{d.dropoff_address}</div>
                  </div>
                  <ArrowRight size={14} className="text-[#71717a] shrink-0" weight="bold" />
                </Link>
              ))}
            </div>
          </Card>
        )}

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-[#2a2a2a] text-[10px] text-[#71717a] uppercase tracking-[0.25em] flex items-center justify-between">
          <span>{company_name}</span>
          <span className="flex items-center gap-1"><Path size={10} className="text-[#ff8c00]" weight="bold" /> Powered by Load Logic</span>
        </div>
      </div>
    </div>
  );
}

function Card({ label, accent = "#ff8c00", children, testid }) {
  return (
    <div className="mb-4 bg-[#0d0d0d] border border-[#2a2a2a]" data-testid={testid}>
      <div className="border-b border-[#2a2a2a] px-4 py-2 flex items-center gap-2">
        <div className="w-1 h-3" style={{ background: accent }} />
        <span className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: accent }}>{label}</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}
